import CrewLayout from './crew-layout.tsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVisitCoverage } from './staff-visit-offers-link.tsx';
import { isHelpPath } from './navigation-path.ts';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSession } from '../state/session.tsx';
import { authApi, billingApi } from '../api/endpoints.ts';
import { ApiRequestError, setPlanRequiredHandler } from '../api/client.ts';
import { clearAllDrafts, suspendDraftWrites } from '../state/draft-store.ts';
import { clearAllInspectionDrafts, suspendInspectionDraftWrites } from '../state/inspection-draft-store.ts';
import { OfflineStatusBadge } from './offline-status-badge.tsx';
import { adoptLocalWalkthroughs } from '../state/local-walkthrough.ts';
import { keyboardHeldByModal } from '../state/modal-stack.ts';
import { Alert, Button, ConfirmDialog } from './ui.tsx';
import { useAppConfig } from './turnstile.tsx';
import { DemoBanner } from './demo-notice.tsx';
import DismissibleTrialBanner from './dismissible-trial-banner.tsx';
import { feedbackApi, messagesApi } from '../api/endpoints.ts';
import { FounderContactLine } from './founder-contact.tsx';
import PreviewBanner from './preview-banner.tsx';
import PublicDemoBanner from './public-demo-banner.tsx';
import NotificationBell from './notification-bell.tsx';
import CommandPalette, { type PaletteShortcut } from './command-palette.tsx';
import { useAiDisclosure } from './ai-disclosure.tsx';
import { useOnlineStatus } from '../hooks/index.ts';
import { getDateLocale } from '../i18n/locale.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import { DEFAULT_CURRENCY_CODE } from '@shared/currency.ts';
import { FREE_PLAN_ID, PLANS, isPlanId } from '@shared/constants/index.ts';
import { companyHasPlanFeature } from './plan-gate.tsx';
import { visibleReportSections } from '../pages/reports/shared.tsx';
import { invalidateQuoteViews } from '../state/quote-cache.ts';
import { jumpToSettingsSection, useSettingsRouter } from './settings-nav.tsx';
import { settingsLeafPath, settingsSectionHref } from './settings/settings-routes.ts';
import { PageErrorBoundary } from './page-error-boundary.tsx';
import type { Company } from '@shared/types/index.ts';
import { Icon } from './icons.tsx';
import { AccountMenu, AppShell, LocationSwitcher, type ShellNavItem } from './app-shell.tsx';
import {
  OFFICE_GROUPS,
  OFFICE_NAV_ITEMS,
  OFFICE_PHONE_TAB_ORDER,
  WORK_HUB_PATH,
  reachableNavItems,
} from './nav-config.ts';

/**
 * What the lock screen needs: whether the door is shut, and how long is left.
 *
 * The Worker answers the first question now (`company.accessLocked`) instead
 * of this deciding for itself. It used to work it out from the trial deadline,
 * which was the whole truth until a Free account could be paused for a year of
 * inactivity — a paused company has no deadline to read, so the old rule
 * rendered an ordinary app whose every request then answered 402. The
 * countdown below is still computed here, because it is a display detail
 * rather than a decision.
 */
function trialState(company: Company | null): {
  daysLeft: number;
  locked: boolean;
  pastDue: boolean;
} {
  if (!company) return { daysLeft: 0, locked: false, pastDue: false };
  if (company.billingStatus === 'active') return { daysLeft: 0, locked: false, pastDue: false };
  if (company.billingStatus === 'past_due') return { daysLeft: 0, locked: false, pastDue: true };
  if (!company.trialEndsAt) return { daysLeft: 0, locked: company.accessLocked, pastDue: false };
  const remaining = Date.parse(company.trialEndsAt) - Date.now();
  return {
    daysLeft: Math.max(0, Math.ceil(remaining / (24 * 60 * 60 * 1000))),
    // The Free plan has no deadline to run out: what it withholds is
    // features, which the gates handle, and the door itself stays open.
    // Choosing Free during a live trial ends that trial, so the status leaves
    // `trialing` and the banner does not stay up. Whether the door is shut
    // is the server's answer (`accessLocked`).
    locked: company.accessLocked,
    pastDue: false,
  };
}

/**
 * Whether the company is inside a trial that still grants the top plan — the
 * Worker's `isLiveTrial` (services/entitlements.ts): status `trialing` and a
 * deadline that is absent or still ahead.
 *
 * Status alone is not enough. A company that signed up on Free keeps
 * `trialing` after its trial runs out, and is not locked, because Free has no
 * deadline; keyed on the status, the banner told it "Free trial — 0 days left.
 * Everything is unlocked." while every gate answered with Free's limits.
 */
function trialIsLive(company: Company | null): boolean {
  if (company?.billingStatus !== 'trialing') return false;
  if (!company.trialEndsAt) return true;
  const ends = Date.parse(company.trialEndsAt);
  return !Number.isFinite(ends) || ends > Date.now();
}

/**
 * How often the lock screen re-asks whether it is still the truth. Slow enough
 * to be free — one small request a minute, from the one screen where nothing
 * else is happening — and fast enough that an operator extending a demo sees it
 * take effect while they are still watching.
 */
const LOCK_REFRESH_INTERVAL_MS = 60_000;

/**
 * What the ⌘K hint on the header button reads as.
 *
 * Decided from the platform rather than the browser: a Mac keyboard has a
 * Command key and a PC one does not, and a hint naming a key the reader
 * cannot see is worse than no hint. `navigator.platform` is deprecated but is
 * still the only synchronous answer available at first render — the fallback
 * is the PC form, which is also what a server-rendered guess would be.
 */
function platformShortcutHint(): string {
  const platform =
    typeof navigator === 'undefined' ? '' : `${navigator.platform ?? ''} ${navigator.userAgent ?? ''}`.toLowerCase();
  return /mac|iphone|ipad|ipod/.test(platform) ? '⌘K' : 'Ctrl K';
}

export { OfflineStatusBadge } from './offline-status-badge.tsx';

export default function AppLayout() {
  return useSession().user?.role === 'crew' ? <CrewLayout /> : <OfficeLayout />;
}

function OfficeLayout() {
  const { t } = useTranslation();
  const { user, company, billingEnforced, demoMode, demoExpirationDays, publicDemo, can, clearSession, refresh } =
    useSession();
  const { currentLocation, locations, switchLocation, impersonatedBy } = useSession();
  useSettingsRouter();
  // Where Subscription is. On the hub it is a page of its own; on the long
  // page the plain `/settings` these links have always used is kept.
  const settingsHub = company?.settingsHub === true;
  const subscriptionHref = settingsSectionHref('billing', settingsHub);
  const billingPagePath = settingsHub ? settingsLeafPath('billing') : '/settings';

  // One notice for every write refused with 403 PLAN_REQUIRED, so individual
  // forms do not each need to know the error code. Named plan comes from the
  // error's fields; the shared catalog turns the id into its display name.
  const [planNotice, setPlanNotice] = useState<{ planName: string } | null>(null);
  useEffect(() => {
    setPlanRequiredHandler((requiredPlan) => {
      setPlanNotice({
        planName: isPlanId(requiredPlan) ? PLANS[requiredPlan].name : PLANS.pro.name,
      });
    });
    return () => setPlanRequiredHandler(null);
  }, []);
  // ⌘K / Ctrl+K anywhere in the app. Registered here rather than in the
  // palette so the shortcut exists whether or not the palette is mounted, and
  // the default is prevented because several browsers bind ⌘K to their own
  // address-bar search.
  const [paletteOpen, setPaletteOpen] = useState(false);
  const shortcutHint = useMemo(platformShortcutHint, []);
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey)) return;
      // Before deciding whether to act on it. The chord is claimed for this app
      // whatever the app then does with it — several browsers bind ⌘K to their
      // own address-bar search, and declining to open the palette while leaving
      // that to run would hand the keyboard to the browser chrome at the one
      // moment it must not go there: out of an open modal, mid-question.
      event.preventDefault();
      // Any dialog holding the keyboard, not one this component happens to know
      // the state of. This listener is on the window, so it hears the shortcut
      // straight through a confirmation's Tab trap, and the palette would open
      // as a SECOND aria-modal layer at that one's stacking level with focus
      // management of its own. Unlike the More sheet below, a confirmation is not
      // closed to make room: nothing may quietly discard a question. See
      // state/modal-stack.ts for what registers and what deliberately does not.
      if (keyboardHeldByModal()) return;
      // Both are portalled, aria-modal dialogs at the same stacking level —
      // leaving the More sheet mounted underneath would leave its focused link
      // trapped behind whichever one the palette effect just focused instead.
      setMoreOpen(false);
      setPaletteOpen((open) => !open);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // The phone's "More" sheet: below `sm` the sidebar is hidden and the bottom
  // bar holds four destinations, and this sheet holds the rest. Closed on
  // every navigation: a menu left open behind the page it just sent someone
  // to is a menu that looks broken the next time it's opened.
  const [moreOpen, setMoreOpen] = useState(false);

  // The nav items this person can actually reach. Computed once and used
  // twice: for the navigation bar, and for the command palette's page
  // shortcuts, which must not offer a screen the bar is hiding.
  // Whether the deployment has the feedback board switched on, and how many
  // lifecycle updates are waiting to be read. One query, shared by react-query
  // with the board itself, so opening Feedback costs nothing extra.
  //
  // `!== false` rather than `=== true`: while the query is in flight the answer
  // is unknown, and hiding a working entry until it lands would make the
  // navigation flicker on every cold load. The API refuses either way.
  const feedbackState = useQuery({
    queryKey: ['feedback-state'],
    queryFn: () => feedbackApi.state(),
    // Not in the shared public demo, which refuses the whole board: it is for
    // customers with an account of their own (shared/public-demo.ts). Asking
    // anyway failed on every page load, and a failed query left the entry up.
    enabled: can('feedback:view') && !publicDemo,
    staleTime: 5 * 60_000,
  });
  const feedbackEnabled = !publicDemo && feedbackState.data?.enabled !== false;
  const feedbackUnread = feedbackState.data?.unreadNotifications ?? 0;

  /**
   * How many unread messages are waiting.
   *
   * Messages, not threads: "4" means four things nobody has read yet, and a
   * customer who sent three texts in a row is three of them. This badge is the
   * size of the pile — the same question the unread count on each row answers,
   * asked about the whole inbox — and a thread written to four times is not one
   * thing to read.
   *
   * It used to say threads, and it did not even say that: it asked for a
   * one-row page of the unread inbox and read the envelope's `total`, which
   * counted every conversation the company had ever had. A company with two
   * unread texts and nine hundred archived threads wore a badge reading 900.
   */
  const waitingState = useQuery({
    queryKey: ['conversations', 'unread-count'],
    queryFn: () => messagesApi.unreadCount(),
    // Wait until the company is known. A missing plan counts as entitled, so
    // asking during that gap would 403 a Free account on every page load.
    enabled: Boolean(company) && can('customers:read') && companyHasPlanFeature(company, 'messaging'),
    staleTime: 60_000,
  });
  const waiting = waitingState.data?.messages ?? 0;

  const appConfig = useAppConfig().data;
  const visitCoverage = useVisitCoverage();
  const visibleNavItems = useMemo(
    () =>
      reachableNavItems(OFFICE_NAV_ITEMS, {
        can,
        company,
        feedbackEnabled,
        helpAssistant: appConfig?.helpAssistant === true,
        visitCoverage,
      }),
    [can, company, feedbackEnabled, appConfig?.helpAssistant, visitCoverage],
  );

  /**
   * What the navigation draws. Only Feedback and Messages carry a count, and
   * only when something is waiting: the count is in the accessible NAME rather
   * than only a floating dot, so it is announced by a screen reader and
   * survives the phone's bar, which renders the same string in a different
   * shape.
   *
   * With the Work hub on (FEATURE_NAV_SECONDARY_HUB), the secondary tools it
   * lists leave the desktop sidebar for the hub page. They stay in the
   * phone's More sheet and the command palette, and their addresses all
   * still work.
   */
  const workHubShown = visibleNavItems.some((item) => item.to === WORK_HUB_PATH);
  const shellItems: ShellNavItem[] = useMemo(
    () =>
      visibleNavItems.map((item) => {
        const count =
          item.labelKey === 'nav.feedback' ? feedbackUnread : item.labelKey === 'nav.messages' ? waiting : 0;
        return {
          to: item.to,
          label: t(item.labelKey),
          icon: item.icon,
          group: item.group,
          hubPath: workHubShown && item.sidebar === 'hubOnly' ? WORK_HUB_PATH : undefined,
          count,
          countLabel:
            count === 0
              ? undefined
              : item.labelKey === 'nav.feedback'
                ? t('nav.feedbackWithUnread', { count })
                : t('nav.messagesWithUnread', { count }),
        };
      }),
    [visibleNavItems, workHubShown, t, feedbackUnread, waiting],
  );
  const shellGroups = useMemo(() => OFFICE_GROUPS.map((group) => ({ id: group.id, label: t(group.labelKey) })), [t]);
  // The logo goes to this person's own landing page — the dashboard for those
  // who have one, quotes for an estimator — rather than to a page their role
  // would refuse.
  const brand = useMemo(() => ({ to: '/', label: t('appLayout.brandedName') }), [t]);

  const onMoreOpenChange = useCallback((open: boolean) => {
    // Mirrors the ⌘K handler: only one of these two modal dialogs should
    // ever be mounted open at once.
    if (open) setPaletteOpen(false);
    setMoreOpen(open);
  }, []);
  const paletteShortcuts: PaletteShortcut[] = useMemo(() => {
    const pages = visibleNavItems.map((item) => ({ to: item.to, label: t(item.labelKey) }));
    // With the reports hub, each report has its own address, so each is a
    // place the palette can go — the ones this company can open, by the same
    // test the hub lists them with.
    if (!company?.reportsHubRoutes || !visibleNavItems.some((item) => item.to === '/reports')) return pages;
    const reports = visibleReportSections(company, companyHasPlanFeature(company, 'reports_core')).map((section) => ({
      to: `/reports/${section}`,
      label: t('reports.hub.paletteLabel', { report: t(`reports.tab.${section}`) }),
    }));
    return [...pages, ...reports];
  }, [visibleNavItems, company, t]);

  const trialDaysGranted = appConfig?.trialDays ?? 14;
  // Falls back to the catalog this app ships with, for the moment before the
  // config query lands — never to a hardcoded currency symbol.
  const pricing = appConfig?.plan ?? {
    currencyCode: DEFAULT_CURRENCY_CODE,
    plans: PLANS,
  };
  // A demo expires too — sooner — so the countdown, the scheduled re-render
  // and the lock screen all have to run on a demo deployment as well as a
  // billed one. Only what they *say* differs.
  const enforced = billingEnforced || demoMode;

  // The trial banner and lock screen are derived from the clock at render
  // time, so a tab left open across the deadline would keep showing the
  // unlocked page forever (a 402-triggered session refetch returns identical
  // data and may not re-render). Schedule a render for the moment the trial
  // ends; trialState() takes it from there. The tick is a dependency so a
  // deadline beyond the browser's ~24.9-day setTimeout cap re-arms in capped
  // hops until it is actually reached.
  const [expiryTick, setExpiryTick] = useState(0);
  useEffect(() => {
    if (!enforced || !company?.trialEndsAt) return;
    const wait = Date.parse(company.trialEndsAt) - Date.now() + 1000;
    if (!Number.isFinite(wait) || wait <= 0) return;
    const timer = window.setTimeout(
      () => {
        setExpiryTick((tick) => tick + 1);
        // Re-render is not enough at the deadline itself: `effectivePlan` is
        // computed server-side and decides every gate in the app, and for a
        // company that chose Free mid-trial it changes precisely now — from the
        // trial's top plan to Free. Without this the shell would keep offering
        // the schedule, the dashboard and the rest from a value that expired a
        // second ago, and each would fail at the API instead of explaining
        // itself. Refetching is cheap and happens once per deadline.
        void refresh();
      },
      Math.min(wait, 2 ** 31 - 1),
    );
    return () => window.clearTimeout(timer);
  }, [enforced, company?.trialEndsAt, expiryTick, refresh]);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (isHelpPath(location.pathname)) return;
    try {
      window.sessionStorage.setItem(`cqb_help_context:${user?.id ?? 'anonymous'}`, location.pathname.slice(0, 120));
    } catch {
      /* Storage is optional. */
    }
  }, [location.pathname, user?.id]);
  const isOnline = useOnlineStatus();

  /**
   * Hands over any walkthrough this browser started with no signal.
   *
   * In the shell rather than on the Magic Quote screen because the cleaner who
   * walked the house is not necessarily the person who opens the app next: the
   * phone finds signal in the driveway and the app is on the schedule, or the
   * quotes list, or nothing at all. The walkthrough should be on the server by
   * the time anybody thinks to look for it, whichever page was open when the
   * connection came back.
   *
   * Cheap when there is nothing to do — one IndexedDB read of an empty store —
   * and single-flighted inside `adoptLocalWalkthroughs`, so the Magic Quote
   * screen asking at the same moment costs one handover, not two.
   */
  const queryClient = useQueryClient();
  // Choosing Free, or a trial ending on Free, recounts series slots on the
  // server before this session updates. Quote views already on screen would
  // keep saying Scheduled until something asks again. The first time this
  // company is seen, leave the cache alone: opening the app on Free has
  // nothing stale to drop.
  const seenEffectivePlan = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const plan = company?.effectivePlan ?? null;
    const previous = seenEffectivePlan.current;
    seenEffectivePlan.current = plan;
    if (previous === undefined || previous === plan) return;
    if (plan !== FREE_PLAN_ID) return;
    invalidateQuoteViews(queryClient);
    // Series settlement runs on the next cron tick (every fifteen minutes).
    // Nudge quote views stale again so an open editor picks up the recount.
    const settlementTimers = [60_000, 5 * 60_000, 15 * 60_000, 30 * 60_000].map((delayMs) =>
      window.setTimeout(() => invalidateQuoteViews(queryClient), delayMs),
    );
    return () => settlementTimers.forEach((timer) => window.clearTimeout(timer));
  }, [company?.effectivePlan, queryClient]);
  // The shell is what hands a walk over when the Magic Quote screen is not
  // open, so the notice has to be able to render from here. `ensure` without
  // force: a declined notice stays down on the minute timer.
  const disclosure = useAiDisclosure();
  const beforeHandover = useRef(disclosure.ensure);
  beforeHandover.current = disclosure.ensure;
  useEffect(() => {
    if (!isOnline) return;
    // Captured for this effect: if the company or branch changes, React tears
    // this down and `isCurrent` goes false — which a handover already in
    // flight reads before its next request rather than carrying on under a
    // cookie that now belongs to somebody else.
    const companyId = company?.id ?? null;
    const locationId = currentLocation?.id ?? null;
    let current = true;
    const handOver = () =>
      void adoptLocalWalkthroughs({
        companyId,
        locationId,
        isCurrent: () => current,
        beforeRequest: () => beforeHandover.current(),
      }).then((results) => {
        if (results.some((result) => result.serverId)) {
          void queryClient.invalidateQueries({ queryKey: ['magic-quotes'] });
        }
      });
    handOver();
    // And again on a timer, because `online` is one event and the connection
    // it announces is often not yet good enough to carry a POST. Without this
    // a handover that failed on the first bar of signal had nothing left to
    // try it again: the capture timer only flushes, and flushing deliberately
    // refuses a local id, so the walkthrough stayed on the phone — unable to
    // be finished — until somebody reloaded. A minute is chosen to be
    // unnoticeable rather than eager; when there is nothing waiting this is
    // one read of an empty store.
    const timer = window.setInterval(handOver, 60_000);
    return () => {
      current = false;
      window.clearInterval(timer);
    };
  }, [isOnline, company?.id, currentLocation?.id, queryClient]);

  const trial = trialState(company);
  // On a demo the banner replaces the trial banner rather than joining it:
  // two countdowns for the same deadline would read as two deadlines.
  const showTrialBanner = billingEnforced && !demoMode && trialIsLive(company) && !trial.locked;
  const showLock =
    enforced && trial.locked && !location.pathname.startsWith('/settings') && !isHelpPath(location.pathname);
  // A Free account paused for a year of inactivity is locked for a different
  // reason than a lapsed trial, and needs a different screen: the trial one
  // explains a deadline this company never had and offers "continue on Free",
  // which it already is — the endpoint would answer success and change
  // nothing, leaving somebody pressing a button that cannot work.
  const pausedForInactivity = company?.accessLockedReason === 'dormant';

  // The lock screen is a dead end for the session query: it replaces the routed
  // app, so no successful request is ever made to invalidate the cached
  // session, and a deadline that moved outward would go unnoticed until a
  // reload. That matters most on a demo, where the documented way back in is an
  // operator raising `demo_expiration_days` and the cron pushing deadlines out
  // — a recovery nobody should have to know to press F5 for. Poll while the
  // lock is on screen and only then; the refetch stops the moment it lifts.
  useEffect(() => {
    if (!showLock) return;
    const timer = window.setInterval(() => void refresh(), LOCK_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [showLock, refresh]);

  // Choosing the Free plan from the lock screen. The session carries the plan
  // that decides every gate in the app, so the refresh below is what actually
  // opens the door — the request only records the choice.
  const [choosingFreePlan, setChoosingFreePlan] = useState(false);
  const [freePlanError, setFreePlanError] = useState<string | null>(null);

  async function continueOnFreePlan() {
    setChoosingFreePlan(true);
    setFreePlanError(null);
    try {
      await billingApi.chooseFree();
      await refresh();
    } catch (error) {
      setFreePlanError(error instanceof ApiRequestError ? error.message : t('appLayout.freePlanError'));
    } finally {
      setChoosingFreePlan(false);
    }
  }

  const [signOutError, setSignOutError] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  // Where a FAILED sign-out was headed, so the retry button goes to the same
  // place. Without it, an operator whose "End and return to admin" failed —
  // offline, or a Worker error — would press retry and be dropped on /login
  // instead of the company page they asked for.
  const [signOutReturnTo, setSignOutReturnTo] = useState<string | null>(null);
  // The confirmation is asked before anything happens, so it holds no session
  // state of its own — only whether the question is up.
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);

  /**
   * Ends the session. `returnTo` is for the impersonation banner: the operator
   * belongs back in the CMS, and a hard navigation is what gets them there
   * with every cached tenant query gone from memory — the router alone would
   * leave this company's data behind in a tab that is no longer theirs.
   *
   * Answers whether the session actually ended, so the confirmation can stay
   * up over a failure and take the retry itself rather than closing onto a
   * banner — see the dialog in the header.
   */
  async function signOut(returnTo?: string): Promise<boolean> {
    setSigningOut(true);
    setSignOutError(null);
    setSignOutReturnTo(returnTo ?? null);

    try {
      await authApi.logout();
    } catch (error) {
      // A 401 means the session is already gone, which is what was wanted.
      // Anything else — offline, a Worker error — means the HttpOnly cookie and
      // the server-side session are still live. Showing the signed-out screen
      // then is a lie that matters most on a shared device: the next person
      // reloads and is signed in as this user.
      if (!(error instanceof ApiRequestError && error.status === 401)) {
        setSigningOut(false);
        setSignOutError(isOnline ? t('appLayout.signOutErrorOnline') : t('appLayout.signOutErrorOffline'));
        return false;
      }
    }

    // Draft writes are refused from here on, so the editor's unmount flush
    // cannot put company data back after the wipe — whenever React gets
    // around to committing it.
    suspendDraftWrites();
    suspendInspectionDraftWrites();
    clearSession();
    if (returnTo) {
      // Drafts first: the navigation ends this document, and anything left
      // unawaited after it may simply not run.
      await Promise.all([clearAllDrafts(), clearAllInspectionDrafts()]);
      window.location.assign(returnTo);
      return true;
    }
    navigate('/login', { replace: true });
    await Promise.all([clearAllDrafts(), clearAllInspectionDrafts()]);
    return true;
  }

  return (
    <AppShell
      items={shellItems}
      groups={shellGroups}
      phoneTabOrder={OFFICE_PHONE_TAB_ORDER}
      brand={brand}
      moreOpen={moreOpen}
      onMoreOpenChange={onMoreOpenChange}
      // Above the header, in the app's loudest color, on every page: an
      // operator wearing somebody else's account should never have to look
      // for the reminder that they are. It only ever renders for the
      // operator — the session carrying the stamp is the one they minted.
      banner={
        impersonatedBy ? (
          <div className="bg-[color:var(--color-danger)] text-white print:hidden">
            <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm">
              <p className="font-medium">
                {t('appLayout.impersonationBanner', {
                  admin: impersonatedBy,
                  user: user?.fullName || user?.email || '',
                })}
              </p>
              <button
                type="button"
                className="rounded-md border border-white/70 px-3 py-1 font-semibold hover:bg-white/10 disabled:opacity-60"
                disabled={signingOut}
                onClick={() => void signOut(`/admin/companies/${user?.companyId ?? ''}`)}
              >
                {t('appLayout.impersonationEnd')}
              </button>
            </div>
          </div>
        ) : null
      }
      topBar={
        <>
          {company?.name ? (
            <p className="flex min-w-0 flex-1 basis-0 items-center gap-2 font-semibold text-[color:var(--color-ink)]">
              <Icon name="company" className="hidden shrink-0 text-[color:var(--color-muted)] sm:block" />
              <span className="truncate">{company.name}</span>
            </p>
          ) : null}
          {locations.length >= 2 ? (
            <LocationSwitcher currentLocation={currentLocation} locations={locations} onSwitch={switchLocation} />
          ) : null}

          <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
            {/* The palette's discoverable half. ⌘K is the fast way in and
                nothing on a screen announces it, so the button carries the
                hint — and is the only way in at all on a touch device. */}
            <button
              type="button"
              className="inline-flex min-h-10 items-center gap-2 rounded-md border border-[color:var(--color-line)] px-2.5 py-1.5 text-sm text-[color:var(--color-muted)] hover:border-[color:var(--color-brand)] hover:text-[color:var(--color-brand)] sm:px-3"
              onClick={() => setPaletteOpen(true)}
              aria-label={t('commandPalette.open')}
            >
              <Icon name="search" size={18} className="shrink-0" />
              <span className="hidden md:inline">{t('commandPalette.open')}</span>
              <kbd className="hidden rounded border border-[color:var(--color-line)] px-1 text-xs md:inline">
                {shortcutHint}
              </kbd>
            </button>
            <NotificationBell />
            {/* Inline from `sm` up. On a phone it takes a line of its own
                under the bar (below): "Using saved data" beside four controls
                and a location switcher is wider than a 320px screen. Each copy
                is display:none at the other width, so only one is ever in the
                accessibility tree. */}
            <span className="hidden sm:contents">
              <OfflineStatusBadge isOnline={isOnline} />
            </span>
            {user ? (
              <AccountMenu
                user={user}
                settings={{ to: '/settings', label: t('nav.settings') }}
                signingOut={signingOut}
                onSignOut={() => setConfirmingSignOut(true)}
              />
            ) : null}
            {confirmingSignOut ? (
              <ConfirmDialog
                title={t('appLayout.signOutConfirmTitle')}
                confirmLabel={t('appLayout.signOut')}
                cancelLabel={t('appLayout.signOutConfirmStay')}
                // The dialog stays up for the whole request and owns both
                // ends of it. Closing on confirm looked tidier and cost a
                // keyboard user their place: the focus this dialog restores
                // on unmount would land on a control that is busy, or gone —
                // i.e. on the document body — and a sign-out that then FAILS
                // puts its error in a banner nothing has focused. Held open,
                // the spinner, the error and the retry are all where the
                // reader already is.
                busy={signingOut}
                error={signOutError}
                onCancel={() => {
                  setConfirmingSignOut(false);
                  // A failure the reader has answered by staying is not news
                  // to repeat in the banner below.
                  setSignOutError(null);
                }}
                onConfirm={() => {
                  void signOut().then((ended) => {
                    // Only on the way out. A failure keeps the question up,
                    // where pressing the confirm again is the retry.
                    if (ended) setConfirmingSignOut(false);
                  });
                }}
              >
                <p>{t('appLayout.signOutConfirmBody')}</p>
              </ConfirmDialog>
            ) : null}
          </div>
        </>
      }
      // Inline from `sm` up, in the bar's controls; on a phone it takes a line
      // of its own (see the inline copy above).
      topBarPhoneLine={<OfflineStatusBadge isOnline={isOnline} />}
      notices={
        <>
          {demoMode ? <DemoBanner days={demoExpirationDays} daysLeft={trial.daysLeft} /> : null}
          {/* Only ever renders inside the shared public demo; see the component. */}
          <PublicDemoBanner />
          {/* Only ever renders inside a feature-preview tenant; see the component. */}
          <PreviewBanner />

          {showTrialBanner ? (
            <DismissibleTrialBanner>
              <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-[color:var(--color-brand-strong)]">
                    {t('appLayout.trialBanner', { count: trial.daysLeft })}
                  </p>
                  {/* Owners only, and smaller than the countdown: the banner is on
                      every page during a trial, and the number is for the person
                      who signed the company up. */}
                  {user?.role === 'owner' ? (
                    <FounderContactLine className="text-xs text-[color:var(--color-brand-strong)]" />
                  ) : null}
                </div>
                {user?.role === 'owner' ? (
                  <Link
                    to={subscriptionHref}
                    className="font-semibold text-[color:var(--color-brand)] hover:underline"
                    onClick={(event) => {
                      // An unmodified primary click is the only one this page should
                      // take. Ctrl, Cmd, Shift, Alt, and the middle button open the
                      // link in a new tab or window, and preventDefault would swallow that.
                      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
                        return;

                      // The section navigator writes the hash with replaceState and
                      // never tells the router, so location.hash goes stale in either
                      // direction. The browser address is the one on screen. When it
                      // already names Subscription — or the router still thinks it
                      // does, after the reader has moved on — scrolling is the whole
                      // visit. Letting the link navigate pushes that same URL again,
                      // and Back then looks like it did nothing.
                      const browserOnSubscription =
                        `${window.location.pathname}${window.location.hash}` === subscriptionHref;
                      const routerOnSubscription = `${location.pathname}${location.hash}` === subscriptionHref;
                      if ((browserOnSubscription || routerOnSubscription) && jumpToSettingsSection('billing')) {
                        event.preventDefault();
                      }
                    }}
                  >
                    {t('appLayout.subscribeInSettings')}
                  </Link>
                ) : null}
              </div>
            </DismissibleTrialBanner>
          ) : null}

          {billingEnforced && !demoMode && company?.billingStatus === 'past_due' ? (
            <div className="border-b border-[color:var(--color-line)] bg-[color:var(--color-warning-soft)] print:hidden">
              <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm">
                <p className="font-medium text-[color:var(--color-warning)]">{t('appLayout.pastDueBanner')}</p>
                {user?.role === 'owner' ? (
                  <Link
                    to={billingPagePath}
                    className="font-semibold text-[color:var(--color-warning)] hover:underline"
                  >
                    {t('appLayout.fixInSettings')}
                  </Link>
                ) : null}
              </div>
            </div>
          ) : null}
        </>
      }
      overlays={
        <>
          {/* The palette navigates as well as searches, so it is given exactly
              the pages this person can reach — the same filter the sidebar
              applies, computed once and shared rather than restated. */}
          <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} shortcuts={paletteShortcuts} />
          {disclosure.dialog}
        </>
      }
    >
      {planNotice ? (
        <div className="mb-4">
          <Alert tone="warning" title={t('appLayout.planRequiredTitle')}>
            <p>
              {t('appLayout.planRequiredNotice', {
                plan: planNotice.planName,
              })}
            </p>
            <div className="mt-2 flex flex-wrap gap-3">
              {user?.role === 'owner' ? (
                <Link
                  to={billingPagePath}
                  className="font-semibold text-[color:var(--color-brand)] hover:underline"
                  onClick={() => setPlanNotice(null)}
                >
                  {t('appLayout.planRequiredGoToBilling')}
                </Link>
              ) : null}
              <Button variant="ghost" onClick={() => setPlanNotice(null)}>
                {t('common.dismiss')}
              </Button>
            </div>
          </Alert>
        </div>
      ) : null}
      {/* Only for a sign-out that was never asked about — the impersonation
            banner's. While the confirmation is up it is showing this same
            error inside itself, and two copies of one failure on one screen
            read as two failures. */}
      {signOutError && !confirmingSignOut ? (
        <div className="mb-4">
          <Alert tone="error" title={t('appLayout.signOutErrorTitle')}>
            <p>{signOutError}</p>
            <Button className="mt-2" busy={signingOut} onClick={() => void signOut(signOutReturnTo ?? undefined)}>
              {t('appLayout.tryAgain')}
            </Button>
          </Alert>
        </div>
      ) : null}
      {showLock ? (
        <div className="card mx-auto max-w-xl text-center">
          <h1 className="text-2xl font-bold">
            {pausedForInactivity
              ? t('appLayout.pausedLockedTitle')
              : demoMode
                ? t('appLayout.demoLockedTitle')
                : t('appLayout.lockedTitle')}
          </h1>
          <p className="mt-3 text-[color:var(--color-muted)]">
            {pausedForInactivity
              ? t('appLayout.pausedLockedMessage')
              : demoMode
                ? t('appLayout.demoLockedMessage', {
                    count: demoExpirationDays,
                  })
                : t('appLayout.lockedMessage', {
                    appName: company?.name ? t('appLayout.brandedName') : t('appLayout.genericName'),
                    trialDays: trialDaysGranted,
                  })}
          </p>
          {pausedForInactivity ? null : (
            <p className="mt-2 text-sm text-[color:var(--color-muted)]">
              {/* No price is quoted on a demo: there is nothing to sell, and
                  the only route back in is the operator extending it. */}
              {demoMode
                ? t('appLayout.demoLockedNote')
                : t('appLayout.lockedPricingNote', {
                    base: formatCents(pricing.plans.starter.basePriceCents, pricing.currencyCode, getDateLocale()),
                  })}
            </p>
          )}
          {/* A lapsed trial is the moment somebody most needs a person.
                A paused free account and a demo have different ways out. */}
          {!demoMode && !pausedForInactivity && user?.role === 'owner' ? (
            <FounderContactLine className="mt-4 text-sm text-[color:var(--color-muted)]" />
          ) : null}
          {/* The way out that costs nothing. Offered on a billed deployment
                only: a demo has no plans at all, and the endpoint refuses
                there for the same reason. Kept next to the paid path rather
                than hidden in Settings, because this screen is where somebody
                decides whether their data is worth going back for. */}
          {!demoMode && !pausedForInactivity && billingEnforced && user?.role === 'owner' ? (
            <div className="mt-6 rounded-lg border border-[color:var(--color-line)] p-4 text-left">
              <p className="font-semibold">{t('appLayout.freePlanTitle')}</p>
              <p className="mt-1 text-sm text-[color:var(--color-muted)]">{t('appLayout.freePlanBody')}</p>
              {freePlanError ? (
                <p className="mt-2 text-sm font-medium text-[color:var(--color-danger)]">{freePlanError}</p>
              ) : null}
              <Button className="mt-3" busy={choosingFreePlan} onClick={() => void continueOnFreePlan()}>
                {t('appLayout.freePlanCta')}
              </Button>
            </div>
          ) : null}

          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {demoMode ? null : user?.role === 'owner' ? (
              <Link to={billingPagePath} className="btn btn-primary">
                {t('appLayout.goToBilling')}
              </Link>
            ) : (
              <p className="text-sm font-medium">{t('appLayout.askOwnerToSubscribe')}</p>
            )}
            {/* Export outlives the lock on purpose, and matters most here:
                  whatever was put into the demo can still be taken back out. */}
            {can('company:export') ? (
              <a href="/api/v1/company/export" className="btn btn-secondary">
                {t('appLayout.exportData')}
              </a>
            ) : null}
          </div>
        </div>
      ) : (
        /* Keyed on the history entry rather than the path, so that
             navigating away from a page that threw is what clears it — and
             "away" includes the same route with a different query, which is
             how this board's own views are addressed (?view=mine), and
             includes going back to a URL already visited. Nothing inside a
             boundary that has caught can navigate, so a changed key is
             always a real navigation. See page-error-boundary.tsx. */
        <PageErrorBoundary resetKey={location.key}>
          <Outlet />
        </PageErrorBoundary>
      )}
    </AppShell>
  );
}
